import React, { useCallback, useEffect, useRef, useState } from 'react';
import Icon from '../../common/icons';
import AssessmentIcon from '../../common/AssessmentIcon';
import annaAvatar from '../../../assets/Anna_Chat_Avatar.png';
import { finishPlan, counselorReason, STATUS } from './finishCases';
import { orderedCrisisLines } from './crisisLines';
import { DeadlineError, releaseStatus } from './sessionApi';
import { pick } from './configs/common';
import { loadClip, useA11yPrefs, useSpeechAvailable } from './a11y';
import { AnswerOutbox, clearWaitingAnswers, isConnectionTrouble, mergeWaiting, signedInUserId } from './answerOutbox';
import { GetAppChatNote } from '../../../pwa/GetAppNudges';
import WorkingCard from './WorkingCard';
import { usePhone } from './usePhone';
import { detectPlatform } from '../../../pwa/platform';
import './testShell.css';

const tx = (lang, en, fil) => (lang === 'fil' ? fil : en);
let entryId = 0;
const nextId = () => {
  entryId += 1;
  return `ts-${entryId}`;
};
const reducedMotion = () => typeof window !== 'undefined' && window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
const timeLabel = (date) =>
  date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true });

const Avatar = ({ hidden }) => (
  <img className={`ts-av${hidden ? ' is-ghost' : ''}`} src={annaAvatar} alt="" aria-hidden="true" />
);

const BotRow = ({ children, ghost, wide, className = '' }) => (
  <div className={`ts-row${wide ? ' is-wide' : ''} ${className}`}>
    <Avatar hidden={ghost} />
    <div className={`ts-stack${wide ? ' is-wide' : ''}`}>{children}</div>
  </div>
);

/** The student's answer; `tag` sits under it (Counted as Yes). */
const MeRow = ({ children, tag = null, className = '' }) => (
  <div className={`ts-row is-me ${className}`}>
    <div className="ts-stack is-me">
      <div className="ts-bubble is-me">{children}</div>
      {tag}
    </div>
  </div>
);

/** The answer words with their numbers, as the intro card lists them. */
const ScaleChips = ({ config, lang }) => (
  <div className="ts-chips">
    {config.scale.map((option) => (
      <span className={`ts-chip${config.layout === 'yesno' ? ' is-plain' : ''}`} key={option.value}>
        {config.layout !== 'yesno' && <b className="ts-chip-num">{option.value}</b>}
        {pick(option.label, lang)}
      </span>
    ))}
  </div>
);

const IntroCard = ({ config, lang, school }) => (
  <div className="ts-card">
    <div className="ts-card-top">
      <span className="ts-tile">
        <AssessmentIcon type={config.key} size={22} />
      </span>
      <div>
        <h3 className="ts-card-title">{pick(config.names.title, lang)}</h3>
        <span className="ts-card-sub">{config.names.code}</span>
      </div>
    </div>
    <div className="ts-card-body">
      <ul className="ts-facts">
        {config.facts(lang, school).map((fact) => (
          <li key={fact.text}>
            <Icon name={fact.icon} size={18} />
            <span>{fact.text}</span>
          </li>
        ))}
      </ul>
      <span className="ts-label">{tx(lang, "You'll answer with", 'Sasagot ka gamit ang')}</span>
      <ScaleChips config={config} lang={lang} />
      {config.introNote && <p className="ts-note">{pick(config.introNote, lang)}</p>}
    </div>
  </div>
);

const CrisisCard = ({ lang }) => (
  <div className="ts-crisis" role="region" aria-label={tx(lang, 'Crisis lines', 'Mga crisis line')}>
    <h4>{tx(lang, 'Need someone right now?', 'Kailangan mo ba ng kausap ngayon?')}</h4>
    {orderedCrisisLines().map((line) => (
      <a className="ts-crisis-line" key={line.id} href={`tel:${line.tel}`}>
        <span className="ts-crisis-name">
          {pick(line.name, lang)}
          <small>
            {pick(line.hours, lang)} · {pick(line.extra, lang)}
          </small>
        </span>
        <span className="ts-crisis-num">
          <Icon name="phone" size={16} />
          {line.number}
        </span>
      </a>
    ))}
  </div>
);

/** "this phone" on a phone, else the iPad or computer the test is open on */
const deviceWord = () => {
  const platform = detectPlatform();
  if (platform.ipad) return 'iPad';
  return platform.mobile ? 'phone' : 'computer';
};

/**
 * Shown while answers are waiting to send: no internet, or a server that is slow to take them.
 * Calm on purpose: the student can keep answering and nothing is lost.
 */
const waitingText = (lang, trouble) => {
  const device = deviceWord();
  return trouble === 'server'
    ? tx(lang,
      `Saving is taking longer than usual. Your answers are kept on this ${device} and will send soon.`,
      `Natatagalan ang pag-save. Naka-save sa ${device} na ito ang mga sagot mo, at ipapadala ang mga ito maya-maya.`)
    : tx(lang,
      `No internet right now. Your answers are kept on this ${device} and will send when you're back online.`,
      `Walang internet ngayon. Naka-save sa ${device} na ito ang mga sagot mo, at ipapadala ang mga ito pagbalik ng internet.`);
};
const WaitingNote = ({ lang, trouble }) => (
  <div className="ts-net-row">
    <div className="ts-net" data-trouble={trouble}>
      <span className="ts-net-icon" aria-hidden="true">
        <Icon name={trouble === 'server' ? 'cloud-upload' : 'wifi-off'} size={16} />
      </span>
      <p>{waitingText(lang, trouble)}</p>
    </div>
  </div>
);

/**
 * One test in the chat: a friendly greeting, an intro card, one numbered question at a time with
 * the answer panel at the bottom, "Change" for the last answer, checkpoints, and a finish card that
 * follows the school's release rule. Each test supplies its words, scale and save calls (configs/).
 */
const TestChat = ({
  config,
  language = 'en',
  schoolName = null,
  studentName = '',
  nextTest = null,
  sessionId: givenSessionId = null,
  savedProgress = null,
  setShowProgressIndicator,
  setCurrentStep,
  setTotalSteps,
  onActiveChange,
  onFinished,
  onFinishAction,
  onReady,
  renderResult,
  context = {},
}) => {
  const lang = language === 'fil' ? 'fil' : 'en';
  const total = config.total;
  const [phase, setPhase] = useState('loading');
  const [index, setIndex] = useState(0);
  const [feed, setFeed] = useState([]);
  const [result, setResult] = useState(null);
  // "Working out your result" between the last answer and the result (tests that define `working`)
  const [working, setWorking] = useState(null);
  const phone = usePhone();
  // Examples start closed on every question, on every screen; "See examples" opens them
  const [showExample, setShowExample] = useState(false);
  const [typed, setTyped] = useState('');
  const [blockedMessage, setBlockedMessage] = useState('');
  // Anna "typing" between an answer and the next question, so it reads like a conversation
  const [thinking, setThinking] = useState(false);
  // The option just tapped stays highlighted while Anna types, so the tap feels answered
  const [picked, setPicked] = useState(null);
  // Going back: the turns after the answer being changed fade out first (their feed position) …
  const [leavingFrom, setLeavingFrom] = useState(null);
  // … then that question lights up again with the earlier answer marked in the panel
  const [backTo, setBackTo] = useState(null);
  // The first messages arrive one after another as the launch screen fades (Chatbot.js)
  const [entering, setEntering] = useState(false);
  // Accessibility: Anna's voice for the current question (Gemini, see a11y.js), the screen-reader
  // line for each new question, and whether the student is answering with a keyboard
  const [a11yPrefs] = useA11yPrefs();
  const speechOn = useSpeechAvailable();
  const [voice, setVoice] = useState({ at: null, state: 'idle' });
  const [announcement, setAnnouncement] = useState('');
  const audioRef = useRef(null);
  const voiceRun = useRef(0);
  const keyboardUser = useRef(false);
  const panelRef = useRef(null);
  const speechLang = config.speechLanguage ? config.speechLanguage(lang) : lang;
  const answersRef = useRef([]);
  const rawsRef = useRef([]);
  const sessionIdRef = useRef(givenSessionId);
  // Every answer goes through the outbox (answerOutbox.js): kept on the phone until the server
  // has it, so a dropped connection loses nothing and the test keeps going
  const outboxRef = useRef(null);
  const [net, setNet] = useState({ waiting: 0, trouble: null, sending: false });
  const aliveRef = useRef(true);
  const endNoteShown = useRef(false);
  const readyDone = useRef(false);
  const busyRef = useRef(false);
  const feedRef = useRef(null);
  const finishedAt = useRef(null);
  const started = useRef(false);
  const timers = useRef([]);
  const lastReplyAt = useRef(-99);
  const replyCount = useRef(0);
  const recentReplies = useRef([]);
  const later = (fn, ms) => {
    const id = window.setTimeout(fn, ms);
    timers.current.push(id);
  };
  useEffect(() => () => timers.current.forEach((id) => window.clearTimeout(id)), []);

  const firstName = String(studentName || '').trim().split(/\s+/)[0] || '';

  // The opening messages are in place: stagger them in and let the launch screen go
  const ready = () => {
    if (readyDone.current) return;
    readyDone.current = true;
    setEntering(true);
    later(() => setEntering(false), 1200);
    if (onReady) onReady();
  };

  const showProgress = useCallback(
    (step) => {
      if (setTotalSteps) setTotalSteps(total);
      if (setCurrentStep) setCurrentStep(step);
      if (setShowProgressIndicator) setShowProgressIndicator(true);
    },
    [setCurrentStep, setShowProgressIndicator, setTotalSteps, total],
  );

  // The outbox for this user and test. One the chat left sending in the background (closed
  // before every answer went) is picked up again, so no answer goes out twice.
  useEffect(() => {
    aliveRef.current = true;
    const owner = signedInUserId();
    // A shared phone keeps no other student's waiting answers
    if (owner) clearWaitingAnswers(owner);
    const outbox = AnswerOutbox.open({
      test: config.key,
      owner,
      sessionId: givenSessionId,
      send: async (record, entry) => {
        const id = await config.save({
          sessionId: record.sessionId || sessionIdRef.current,
          nextIndex: entry.step,
          answers: record.answers,
          raws: record.raws,
          context,
        });
        if (id) sessionIdRef.current = id;
        return id;
      },
      // Only a real change re-renders the chat (the outbox reports on every send)
      onChange: (next) =>
        setNet((prev) => (prev.waiting === next.waiting && prev.trouble === next.trouble && prev.sending === next.sending ? prev : next)),
    });
    outboxRef.current = outbox;
    return () => {
      aliveRef.current = false;
      outbox.detach();
    };
  }, []);

  // Load saved progress once: resume on the next question, or greet and show the intro card.
  // Answers still waiting on this phone (the connection dropped, or the app closed before they
  // went) join the server's progress and are sent first.
  useEffect(() => {
    if (started.current) return;
    started.current = true;
    if (setShowProgressIndicator) setShowProgressIndicator(false);
    (async () => {
      let point = { sessionId: givenSessionId, nextIndex: 0, answers: [] };
      let fromServer = true;
      for (;;) {
        try {
          point = await config.load({ sessionId: givenSessionId, savedProgress, context });
          break;
        } catch (error) {
          if (error instanceof DeadlineError) {
            setBlockedMessage(error.message);
            setPhase('blocked');
            ready();
            return;
          }
          // Couldn't read progress: start fresh; the first save starts a session if needed
          if (!isConnectionTrouble(error) || !outboxRef.current) break;
          // No connection: carry on from the answers kept on this phone …
          if (outboxRef.current.pending()) {
            fromServer = false;
            break;
          }
          // … or, with none kept here, wait for it rather than start over
          ready();
          await outboxRef.current.nextChance('offline');
          if (!aliveRef.current) return;
        }
      }
      if (!aliveRef.current) return;
      const outbox = outboxRef.current;
      const kept = outbox ? outbox.pending() : null;
      const merged = mergeWaiting(fromServer || !kept ? point : { ...point, sessionId: kept.sessionId || point.sessionId }, kept);
      point = merged.point;
      sessionIdRef.current = point.sessionId || givenSessionId || null;
      answersRef.current = Array.isArray(point.answers) ? [...point.answers] : [];
      rawsRef.current = Array.isArray(point.raws) ? [...point.raws] : [];
      if (outbox) {
        outbox.restore({
          sessionId: sessionIdRef.current,
          step: point.nextIndex,
          answers: answersRef.current,
          raws: rawsRef.current,
          entries: merged.entries,
        });
      }
      if (point.nextIndex >= total) {
        // Every answer is saved but the result never was (e.g. the tab closed at the end)
        setFeed([{ id: nextId(), kind: 'bot', text: tx(lang, 'Welcome back. All your answers are saved, so here is your result.', 'Welcome back. Naka-save ang lahat ng sagot mo, kaya narito ang iyong resulta.') }]);
        finish(answersRef.current);
        ready();
        return;
      }
      if (point.nextIndex > 0) {
        const last = point.nextIndex - 1;
        const welcome = firstName
          ? tx(lang,
            `Welcome back, ${firstName}. You stopped at question ${point.nextIndex + 1}. Your first ${point.nextIndex} answers are saved.`,
            `Welcome back, ${firstName}! Nasa tanong ${point.nextIndex + 1} ka. Naka-save na ang unang ${point.nextIndex} mong sagot.`)
          : tx(lang,
            `Welcome back. You stopped at question ${point.nextIndex + 1}. Your first ${point.nextIndex} answers are saved.`,
            `Welcome back! Nasa tanong ${point.nextIndex + 1} ka. Naka-save na ang unang ${point.nextIndex} mong sagot.`);
        const entries = [{ id: nextId(), kind: 'bot', text: welcome }];
        if (answersRef.current[last] !== undefined && answersRef.current[last] !== null) {
          entries.push({ id: nextId(), kind: 'qa', index: last, value: answersRef.current[last], compact: true });
        }
        setFeed(entries);
        setIndex(point.nextIndex);
        setShowExample(false);
        setPhase('active');
        showProgress(point.nextIndex + 1);
      } else {
        setFeed([
          { id: nextId(), kind: 'bot', text: pick(config.greeting, lang) },
          { id: nextId(), kind: 'intro' },
        ]);
        setPhase('intro');
      }
      ready();
    })();
    // Runs once per mounted test
  }, []);

  // Let the chat know whether leaving now would interrupt a test
  useEffect(() => {
    if (onActiveChange) onActiveChange({ active: phase === 'active' || phase === 'saving', question: index + 1, key: config.key });
  }, [phase, index]);

  // A released result with its own screen (RIASEC) owns the feed and places it itself
  const resultOwnsFeed = phase === 'finished' && Boolean(renderResult) && result?.status === STATUS.RELEASED && !result?.worrying;

  // Keep the newest message in view
  useEffect(() => {
    const el = feedRef.current;
    if (!el || resultOwnsFeed) return;
    const id = window.requestAnimationFrame(() => {
      el.scrollTo({ top: el.scrollHeight, behavior: 'smooth' });
    });
    return () => window.cancelAnimationFrame(id);
  }, [feed, phase, index, showExample, result, net.trouble]);

  // Turning Larger text on or off keeps the latest message in view
  useEffect(() => {
    const el = feedRef.current;
    if (!el || phase === 'finished') return undefined;
    let inner = 0;
    const outer = window.requestAnimationFrame(() => {
      inner = window.requestAnimationFrame(() => {
        el.scrollTop = el.scrollHeight;
      });
    });
    return () => {
      window.cancelAnimationFrame(outer);
      window.cancelAnimationFrame(inner);
    };
  }, [a11yPrefs.largeText]);

  // The window changing size (big ↔ corner, phone rotation) keeps the current question in view
  // when the student was already at the bottom
  useEffect(() => {
    const el = feedRef.current;
    if (!el || phase === 'finished' || typeof ResizeObserver === 'undefined') return undefined;
    let atBottom = true;
    const onScroll = () => {
      atBottom = el.scrollHeight - el.scrollTop - el.clientHeight < 48;
    };
    const observer = new ResizeObserver(() => {
      if (atBottom) el.scrollTop = el.scrollHeight;
    });
    el.addEventListener('scroll', onScroll, { passive: true });
    observer.observe(el);
    return () => {
      el.removeEventListener('scroll', onScroll);
      observer.disconnect();
    };
  }, [phase === 'finished']);

  // Answers waiting to send: screen readers hear the note once, and if the test is already over,
  // Anna says she'll finish once they're in (one line, kept in the thread)
  useEffect(() => {
    if (!net.trouble || phase === 'finished' || phase === 'blocked') return;
    setAnnouncement(waitingText(lang, net.trouble));
    if (phase !== 'saving' || endNoteShown.current) return;
    endNoteShown.current = true;
    setFeed((prev) => [
      ...prev,
      {
        id: nextId(),
        kind: 'bot',
        text: net.trouble === 'server'
          ? tx(lang,
            "Almost done. I'll wrap up as soon as your answers go through.",
            'Malapit na tayo. Tatapusin ko ito kapag naipadala na ang mga sagot mo.')
          : tx(lang,
            "Almost done. I'll wrap up as soon as you're back online.",
            'Malapit na tayo. Tatapusin ko ito pagbalik ng internet.'),
      },
    ]);
  }, [net.trouble, phase]);

  const start = () => {
    if (phase !== 'intro') return;
    setIndex(0);
    setShowExample(false);
    setPhase('active');
    showProgress(1);
  };

  /**
   * A short reply that matches the answer: a checkpoint at set points, otherwise the test's own
   * line, spaced out so it never chatters (configs decide the words; replies never praise or
   * judge an answer, so they don't nudge the next one).
   */
  const replyFor = (current, value, answered) => {
    const checkpoint = config.checkpoints?.[answered];
    if (checkpoint) return { text: pick(checkpoint, lang), checkpoint: true };
    if (!config.reply) return null;
    // Not a line used in the last four replies: try the next wordings, else stay quiet
    let reply = null;
    for (let k = 0; k < 4 && !reply; k += 1) {
      const candidate = config.reply(current, value, lang, replyCount.current + k);
      if (candidate && candidate.text && !recentReplies.current.includes(candidate.text)) reply = candidate;
    }
    if (!reply) return null;
    const gap = reply.gap ?? (reply.care ? 3 : 5);
    if (answered - lastReplyAt.current < gap) return null;
    return reply;
  };

  /**
   * The last answer is in: every waiting answer is sent first, then the result is saved. Without
   * a connection this waits (the note says why) and finishes once the phone is back online; the
   * "Not saved yet" card is only for a save the server turned down.
   */
  const finish = async (finalAnswers) => {
    setPhase('saving');
    // "Working out your result": the real steps instead of typing dots, shown for at least a moment
    const startedAt = Date.now();
    let slowTimer = null;
    if (config.working) {
      setWorking({ step: config.working.steps?.[0]?.key || null, slow: false });
      slowTimer = window.setTimeout(() => setWorking((w) => (w ? { ...w, slow: true } : w)), 8000);
    }
    const stopWorking = () => {
      if (slowTimer) window.clearTimeout(slowTimer);
      setWorking(null);
    };
    const onProgress = (step) => setWorking((w) => (w ? { ...w, step } : w));
    const outbox = outboxRef.current;
    let saved = false;
    let data = null;
    for (;;) {
      if (outbox) await outbox.whenEmpty();
      if (!aliveRef.current) return stopWorking();
      // Never finish one student's test under another student's sign-in
      if (outbox && outbox.ownerChanged()) break;
      try {
        const out = await config.complete({ sessionId: sessionIdRef.current || outbox?.sessionId, answers: finalAnswers, raws: rawsRef.current, context, onProgress });
        const id = out && typeof out === 'object' ? out.sessionId : out;
        data = out && typeof out === 'object' ? out.data || null : null;
        if (id) sessionIdRef.current = id;
        saved = true;
        break;
      } catch (error) {
        if (!outbox || !isConnectionTrouble(error)) break;
        await outbox.nextChance(error?.response ? 'server' : 'offline');
        if (!aliveRef.current) return stopWorking();
      }
    }
    if (outbox) outbox.settled();
    let status = STATUS.SAVE_FAILED;
    if (saved) {
      try {
        status = config.releaseStatus
          ? await config.releaseStatus(sessionIdRef.current)
          : await releaseStatus(config.key, sessionIdRef.current);
      } catch (error) {
        // The answers are saved; the card falls back to "a counselor looks first"
        status = STATUS.IN_REVIEW;
      }
    }
    if (!aliveRef.current) return stopWorking();
    // Long enough to read, never a flash
    if (config.working && !reducedMotion()) {
      const left = 1200 - (Date.now() - startedAt);
      if (left > 0) await new Promise((resolve) => window.setTimeout(resolve, left));
      if (!aliveRef.current) return stopWorking();
    }
    stopWorking();
    const worrying = status !== STATUS.SAVE_FAILED && Boolean(config.isWorrying && config.isWorrying(finalAnswers));
    finishedAt.current = new Date();
    if (setShowProgressIndicator) setShowProgressIndicator(false);
    setResult({ status, worrying, answers: finalAnswers, sessionId: sessionIdRef.current, data });
    setPhase('finished');
    if (status !== STATUS.SAVE_FAILED && onFinished) onFinished({ key: config.key, status, worrying });
  };

  const stopVoice = () => {
    voiceRun.current += 1;
    if (audioRef.current) audioRef.current.pause();
    setVoice({ at: null, state: 'idle' });
  };
  // Reads question `at` aloud in Anna's voice; a newer request or Stop cancels an older one
  const playQuestion = async (at) => {
    voiceRun.current += 1;
    const run = voiceRun.current;
    if (audioRef.current) audioRef.current.pause();
    setVoice({ at, state: 'loading' });
    try {
      const url = await loadClip(config.questionText(at, lang), speechLang);
      if (voiceRun.current !== run) return;
      if (!audioRef.current) audioRef.current = new Audio();
      const audio = audioRef.current;
      audio.src = url;
      audio.onended = () => {
        if (voiceRun.current === run) setVoice({ at: null, state: 'idle' });
      };
      await audio.play();
      if (voiceRun.current === run) setVoice({ at, state: 'playing' });
    } catch (error) {
      if (voiceRun.current === run) setVoice({ at, state: 'error' });
    }
  };
  useEffect(() => () => {
    voiceRun.current += 1;
    if (audioRef.current) audioRef.current.pause();
  }, []);

  const answer = (value, raw = null) => {
    if (phase !== 'active' || busyRef.current || thinking) return;
    stopVoice();
    const current = index;
    const answers = [...answersRef.current];
    answers[current] = value;
    answersRef.current = answers;
    const raws = [...rawsRef.current];
    raws[current] = raw;
    rawsRef.current = raws;
    const answered = current + 1;
    setBackTo(null);
    // Answering settles the welcome-back choice, so its buttons go
    setFeed((prev) => [
      ...prev.map((entry) => (entry.actions ? { ...entry, actions: null } : entry)),
      { id: nextId(), kind: 'qa', index: current, value, raw },
    ]);
    setTyped('');
    outboxRef.current.add({ index: current, value, raw, step: answered, answers, raws });
    if (answered >= total) {
      finish(answers);
      return;
    }
    const reply = replyFor(current, value, answered);
    const fast = reducedMotion();
    busyRef.current = true;
    setPicked(value);
    setThinking(true);
    later(() => {
      if (reply) {
        setFeed((prev) => [...prev, { id: nextId(), kind: 'bot', text: reply.text, checkpoint: reply.checkpoint, care: reply.care }]);
        lastReplyAt.current = answered;
        replyCount.current += 1;
        recentReplies.current = [...recentReplies.current, reply.text].slice(-4);
      }
      later(() => {
        setIndex(answered);
        setShowExample(false);
        if (setCurrentStep) setCurrentStep(answered + 1);
        setThinking(false);
        setPicked(null);
        busyRef.current = false;
      }, reply ? (fast ? 60 : 420) : 0);
    }, fast ? 80 : 360);
  };

  // Back one question: everything after that answer fades out while the chat scrolls back to it,
  // then the question is current again and the panel marks the answer given before
  const lastQa = [...feed].reverse().find((entry) => entry.kind === 'qa' && !entry.compact);
  const canChange = phase === 'active' && !thinking && leavingFrom === null && lastQa && lastQa.index === index - 1;
  const change = () => {
    if (!canChange || busyRef.current) return;
    const target = lastQa;
    const at = feed.findIndex((entry) => entry.id === target.id);
    const fast = reducedMotion();
    busyRef.current = true;
    stopVoice();
    setLeavingFrom(at);
    // Scroll to where that question ends up once the rest is gone, so nothing jumps afterwards
    const el = feedRef.current;
    const bubble = el && el.querySelector(`[data-q="${target.index}"]`);
    if (el && bubble) {
      const row = bubble.closest('.ts-row') || bubble;
      // Larger text zooms the view; rects are on screen, scrolling is in the feed's own pixels
      const zoom = el.currentCSSZoom || 1;
      const bottom = (row.getBoundingClientRect().bottom - el.getBoundingClientRect().top) / zoom + el.scrollTop;
      const pad = parseFloat(window.getComputedStyle(el).paddingBottom) || 0;
      el.scrollTo({ top: Math.max(0, bottom + pad - el.clientHeight), behavior: fast ? 'auto' : 'smooth' });
    }
    later(() => {
      setFeed((prev) => prev.filter((entry, i) => i < at));
      setIndex(target.index);
      setShowExample(false);
      setBackTo({ index: target.index, value: target.value });
      setLeavingFrom(null);
      busyRef.current = false;
      if (setCurrentStep) setCurrentStep(target.index + 1);
    }, fast ? 0 : 240);
  };

  const sendTyped = (event) => {
    event.preventDefault();
    const text = typed.trim();
    if (!text || !config.typing || thinking) return;
    const value = config.typing.match(text, index);
    if (value === null || value === undefined) {
      setFeed((prev) => [
        ...prev,
        { id: nextId(), kind: 'me', text },
        { id: nextId(), kind: 'bot', text: pick(config.typing.reask, lang) },
      ]);
      setAnnouncement(pick(config.typing.reask, lang));
      setTyped('');
      return;
    }
    answer(value, text);
  };

  // Keys answer on a keyboard: 0-3 for DASS-42, 1-7 for Ryff …, Y / N for Career interests
  // (O / H too in Filipino)
  useEffect(() => {
    if (phase !== 'active') return undefined;
    const onKey = (event) => {
      if (event.altKey || event.ctrlKey || event.metaKey) return;
      const tag = (event.target?.tagName || '').toLowerCase();
      if (tag === 'input' || tag === 'textarea') return;
      if (thinking) return;
      if (config.layout === 'yesno') {
        const key = String(event.key || '').toLowerCase();
        const value = key === 'y' || (lang === 'fil' && key === 'o') ? 'yes' : key === 'n' || (lang === 'fil' && key === 'h') ? 'no' : null;
        if (value) answer(value);
        return;
      }
      const option = config.scale.find((o) => String(o.value) === event.key);
      if (option) answer(option.value);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  });

  // Keyboard or pointer: decides whether a new question moves focus to its answers
  useEffect(() => {
    const onKey = (event) => {
      if (!event.altKey && !event.ctrlKey && !event.metaKey) keyboardUser.current = true;
    };
    const onPointer = () => {
      keyboardUser.current = false;
    };
    window.addEventListener('keydown', onKey, true);
    window.addEventListener('pointerdown', onPointer, true);
    return () => {
      window.removeEventListener('keydown', onKey, true);
      window.removeEventListener('pointerdown', onPointer, true);
    };
  }, []);

  const questionShown = phase === 'active' && !thinking && leavingFrom === null;

  // Keyboard users land on the answers of each new question (typing keeps the text box)
  useEffect(() => {
    if (!questionShown || !keyboardUser.current) return;
    const panel = panelRef.current;
    if (!panel) return;
    const focused = document.activeElement;
    if (focused && focused.tagName === 'INPUT' && panel.contains(focused)) return;
    const first = panel.querySelector('.ts-opt');
    if (first) first.focus({ preventScroll: true });
  }, [questionShown, index]);

  // Screen readers hear Anna's latest line and the new question once, not every typing dot
  useEffect(() => {
    if (!questionShown) return;
    const lastAnswer = feed.map((entry) => entry.kind).lastIndexOf('qa');
    const said = feed.slice(lastAnswer + 1).filter((entry) => entry.kind === 'bot').map((entry) => entry.text).join(' ');
    const number = tx(lang, `Question ${index + 1} of ${total}.`, `Tanong ${index + 1} sa ${total}.`);
    setAnnouncement(`${said ? `${said} ` : ''}${number} ${config.questionText(index, lang)}`);
  }, [questionShown, index]);

  // Read questions aloud: each new question is read as it appears, and the next one is fetched
  // ahead so it starts at once
  useEffect(() => {
    if (!questionShown || !speechOn || !a11yPrefs.readAloud) return;
    playQuestion(index);
    if (index + 1 < total) loadClip(config.questionText(index + 1, lang), speechLang).catch(() => {});
  }, [questionShown, index, speechOn, a11yPrefs.readAloud]);
  useEffect(() => {
    if (!a11yPrefs.readAloud) stopVoice();
  }, [a11yPrefs.readAloud]);

  // Arrow keys move between the answers
  const moveFocus = (event) => {
    if (!['ArrowRight', 'ArrowDown', 'ArrowLeft', 'ArrowUp'].includes(event.key)) return;
    const options = [...event.currentTarget.querySelectorAll('.ts-opt')];
    const at = options.indexOf(document.activeElement);
    if (at < 0) return;
    event.preventDefault();
    const step = event.key === 'ArrowRight' || event.key === 'ArrowDown' ? 1 : -1;
    options[(at + step + options.length) % options.length].focus();
  };

  const act = (kind) => {
    if (kind === 'retry') {
      finish(answersRef.current);
      return;
    }
    if (kind === 'start') {
      start();
      return;
    }
    if (kind === 'keep') {
      setFeed((prev) => prev.map((entry) => (entry.actions ? { ...entry, actions: null } : entry)));
      return;
    }
    if (!onFinishAction) return;
    onFinishAction(kind, {
      key: config.key,
      status: result?.status,
      worrying: Boolean(result?.worrying),
      resultsTab: config.resultsTab || config.key,
      nextKey: nextTest?.key || null,
      reason: counselorReason({
        status: result?.status,
        worrying: result?.worrying,
        followupReason: config.followupReason,
        fallbackReason: config.counselorFallbackReason || 'general',
      }),
    });
  };

  // ---------- feed ----------
  /*
   * A real chat thread: each question is one Anna bubble that keeps its place and shape from the
   * moment it appears. Answering only greys it out (same key, so React keeps the same element),
   * the answer follows as the student's bubble, and Anna's reply comes right under that answer.
   * "Change" sits beside the latest answer only. Anna's avatar shows once per run of her bubbles.
   */
  const examples = phase === 'active' ? config.examples(index, lang) : [];
  const thread = [];
  let lastSpeaker = null;
  const pushBot = (key, content, { wide = false, ghost = false, className = '', leaving = false } = {}) => {
    thread.push(
      <BotRow key={key} wide={wide} ghost={ghost || lastSpeaker === 'bot'} className={`${className}${leaving ? ' is-leaving' : ''}`}>
        {content}
      </BotRow>,
    );
    lastSpeaker = 'bot';
  };
  const pushQuestion = (at, current, leaving = false) => {
    pushBot(
      `q-${at}`,
      <div className={`ts-bubble ts-q${current ? ' is-current' : ''}${current && backTo?.index === at ? ' is-back' : ''}${current && voice.at === at && voice.state === 'playing' ? ' is-speaking' : ''}`} data-q={at}>
        <span className="ts-qnum">{tx(lang, `${at + 1} of ${total}`, `${at + 1} sa ${total}`)}</span>
        <p className="ts-qtext">{config.questionText(at, lang)}</p>
      </div>,
      { className: 'is-question', leaving },
    );
  };
  const goingBack = leavingFrom !== null;

  feed.forEach((entry, pos) => {
    const leaving = goingBack && pos > leavingFrom;
    if (entry.kind === 'bot') {
      pushBot(
        entry.id,
        <>
          <div className={`ts-bubble${entry.checkpoint ? ' is-checkpoint' : ''}${entry.care ? ' is-care' : ''}`}>{entry.text}</div>
          {entry.actions && (
            <div className="ts-quick">
              {entry.actions.map((action, i) => (
                <button type="button" key={action.kind} className={i === 0 ? 'is-main' : ''} onClick={() => act(action.kind)}>
                  {action.label}
                </button>
              ))}
            </div>
          )}
        </>,
        { leaving },
      );
      return;
    }
    if (entry.kind === 'me') {
      thread.push(<MeRow key={entry.id} className={leaving ? 'is-leaving' : ''}>{entry.text}</MeRow>);
      lastSpeaker = 'me';
      return;
    }
    if (entry.kind === 'intro') {
      pushBot(entry.id, <IntroCard config={config} lang={lang} school={schoolName} />, { wide: true, ghost: true });
      return;
    }
    if (entry.kind === 'qa') {
      pushQuestion(entry.index, false, leaving);
      const typedAnswer = Boolean(entry.raw && config.typing);
      const echo = config.echo(entry.value, lang);
      thread.push(
        <MeRow
          key={`a-${entry.id}`}
          className={goingBack && pos >= leavingFrom ? 'is-leaving' : ''}
          tag={typedAnswer ? <span className="ts-counted">{config.typing.countedTag(entry.value, lang)}</span> : null}
        >
          {typedAnswer ? entry.raw : echo}
        </MeRow>,
      );
      lastSpeaker = 'me';
    }
  });
  // The full conversation so far, for a result screen that takes over the view (RIASEC)
  const history = [...thread];
  const historyEndsWithAnna = lastSpeaker === 'bot';

  // The current question joins the same list, so answering it keeps the very same bubble
  if (phase === 'active' && !thinking) {
    pushQuestion(index, true, goingBack);
    if (examples.length > 0 || speechOn) {
      const speaking = voice.at === index ? voice.state : 'idle';
      const listenLabel = {
        idle: tx(lang, 'Listen', 'Pakinggan'),
        loading: tx(lang, 'Loading…', 'Sandali…'),
        playing: tx(lang, 'Stop', 'Ihinto'),
        error: tx(lang, 'Try again', 'Subukan ulit'),
      }[speaking];
      pushBot(
        `ex-${index}`,
        <div className="ts-ex">
          <div className="ts-ex-actions">
            {examples.length > 0 && (
              <button
                type="button"
                className={`ts-example-btn${showExample ? ' is-open' : ''}`}
                aria-expanded={showExample}
                onClick={() => setShowExample((open) => !open)}
              >
                <Icon name="lightbulb" size={15} />
                {showExample
                  ? tx(lang, examples.length > 1 ? 'Hide the examples' : 'Hide the example', 'Itago ang halimbawa')
                  : tx(lang, examples.length > 1 ? 'See examples' : 'See an example', examples.length > 1 ? 'Tingnan ang mga halimbawa' : 'Tingnan ang halimbawa')}
              </button>
            )}
            {speechOn && (
              <button
                type="button"
                className={`ts-listen is-${speaking}`}
                onClick={() => (speaking === 'playing' || speaking === 'loading' ? stopVoice() : playQuestion(index))}
                aria-label={speaking === 'playing' ? tx(lang, 'Stop reading', 'Ihinto ang pagbasa') : tx(lang, 'Read this question aloud', 'Basahin nang malakas ang tanong')}
              >
                {speaking === 'playing' ? (
                  <span className="ts-bars" aria-hidden="true">
                    <i />
                    <i />
                    <i />
                    <i />
                  </span>
                ) : speaking === 'loading' ? (
                  <span className="ts-spin" aria-hidden="true" />
                ) : (
                  <Icon name="volume" size={15} />
                )}
                <span>{listenLabel}</span>
              </button>
            )}
          </div>
          {showExample && examples.length > 0 && (
            <ul className="ts-examples">
              {examples.map((example) => (
                <li key={example}>{example}</li>
              ))}
            </ul>
          )}
        </div>,
        { className: 'is-examples', leaving: goingBack },
      );
    }
  }

  const plan = result ? finishPlan({ status: result.status, worrying: result.worrying, nextTest }) : null;
  const school = schoolName;

  const finishMessage = () => {
    if (!plan) return '';
    switch (plan.message) {
      case 'released':
        return tx(lang,
          'Your results are ready. Your Results page explains them in plain words.',
          'Handa na ang iyong mga resulta. Ipinapaliwanag ito nang simple sa iyong Results page.');
      case 'not_shared':
        return school
          ? tx(lang,
            `Your answers are saved. ${school} isn't showing these results to you right now. A counselor can go through them with you.`,
            `Naka-save ang iyong mga sagot. Hindi muna ipinapakita ng ${school} ang mga resultang ito sa mga estudyante. Puwede mo itong pag-usapan kasama ang isang counselor.`)
          : tx(lang,
            "Your answers are saved. These results aren't being shown right now. A counselor can go through them with you.",
            'Naka-save ang iyong mga sagot. Hindi muna ipinapakita ang mga resultang ito sa mga estudyante. Puwede mo itong pag-usapan kasama ang isang counselor.');
      case 'save_failed':
        return tx(lang,
          "Your last answers didn't save. Check your internet and we'll try again.",
          'Hindi na-save ang huli mong mga sagot. Tingnan ang iyong internet at subukan ulit.');
      case 'in_review':
      default:
        return school
          ? tx(lang,
            `A ${school} counselor looks at your answers first. They'll show on your Results page once they're released.`,
            `Titingnan muna ng isang ${school} counselor ang iyong mga sagot. Lalabas ang mga ito sa iyong Results page kapag na-release na.`)
          : tx(lang,
            "A counselor looks at your answers first. They'll show on your Results page once they're released.",
            'Titingnan muna ng isang counselor ang iyong mga sagot. Lalabas ang mga ito sa iyong Results page kapag na-release na.');
    }
  };

  // Screen readers hear how the test ended
  useEffect(() => {
    if (phase !== 'finished' || !plan) return;
    setAnnouncement(plan.message === 'worry'
      ? tx(lang,
        'That was the last question. Some of your answers tell us things have been heavy lately. Booking and crisis lines are below.',
        'Iyan na ang huling tanong. May mga sagot ka na nagsasabing mabigat ang pinagdadaanan mo. Nasa ibaba ang booking at mga crisis line.')
      : finishMessage());
  }, [phase, plan && plan.message]);

  const renderFinish = () => {
    if (!result) return null;
    if (plan.message === 'worry') {
      const office = school ? `the ${school} guidance office` : 'the guidance office';
      return (
        <>
          <BotRow>
            <div className="ts-bubble">
              {tx(lang,
                "That was the last question. Thank you for answering honestly. That's not easy.",
                'Iyan na ang huling tanong. Salamat sa tapat mong pagsagot. Hindi iyon madali.')}
            </div>
          </BotRow>
          <BotRow>
            <div className="ts-bubble">
              {tx(lang,
                `Some of your answers tell us things have been really heavy lately. Please talk to someone soon. You can book a time with ${office} now, or call one of these lines whenever you need to.`,
                `May mga sagot ka na nagsasabing mabigat ang pinagdadaanan mo nitong mga nakaraang araw. Makipag-usap ka sana sa isang tao. Puwede kang mag-book ngayon sa ${school ? `${school} guidance office` : 'guidance office'}, o tumawag sa isa sa mga linyang ito kahit kailan.`)}
            </div>
          </BotRow>
          <BotRow ghost wide>
            <CrisisCard lang={lang} />
          </BotRow>
        </>
      );
    }
    return (
      <>
        {plan.message !== 'save_failed' && (
          <BotRow>
            <div className="ts-bubble">
              {tx(lang,
                `That's all ${total}. Thank you for answering honestly.`,
                `Iyan na ang lahat ng ${total}. Salamat sa tapat mong pagsagot.`)}
            </div>
          </BotRow>
        )}
        <BotRow ghost wide>
          <div className={`ts-card${plan.message === 'save_failed' ? ' is-warn' : ''}`}>
            <div className="ts-card-top">
              <span className="ts-tile">
                <Icon name={plan.message === 'save_failed' ? 'alert-circle' : 'check'} size={20} />
              </span>
              <div>
                <h3 className="ts-card-title">
                  {plan.message === 'save_failed'
                    ? tx(lang, 'Not saved yet', 'Hindi pa naka-save')
                    : tx(lang, `${pick(config.names.title, lang)} done`, `Tapos na ang ${pick(config.names.title, lang)}`)}
                </h3>
                {plan.message !== 'save_failed' && finishedAt.current && (
                  <span className="ts-card-sub">{tx(lang, 'Saved at', 'Na-save noong')} {timeLabel(finishedAt.current)}</span>
                )}
              </div>
            </div>
            <div className="ts-card-body">
              <p>{finishMessage()}</p>
              {plan.allDone && (
                <p className="ts-note">{tx(lang, "That's every test for now.", 'Iyan na ang lahat ng test sa ngayon.')}</p>
              )}
            </div>
          </div>
        </BotRow>
        {/* After the last test, on phones: "Get the ANNA app" (src/pwa) */}
        {plan.allDone && plan.message !== 'save_failed' && (
          <GetAppChatNote lang={lang} results={plan.message} Row={BotRow} />
        )}
      </>
    );
  };

  // ---------- footer ----------
  const buttonLabel = (kind) => {
    switch (kind) {
      case 'results':
        return { icon: 'arrow-right', text: tx(lang, 'See my results', 'Tingnan ang aking resulta') };
      case 'next':
        return { test: nextTest?.key, text: tx(lang, `Next: ${nextTest?.title || ''}`, `Susunod: ${nextTest?.title || ''}`) };
      case 'counselor':
        return { icon: 'calendar', text: tx(lang, 'Talk to a counselor', 'Kausapin ang counselor') };
      case 'book':
        return { icon: 'calendar', text: tx(lang, 'Book a time now', 'Mag-book ngayon') };
      case 'retry':
        return { icon: 'refresh', text: tx(lang, 'Try again', 'Subukan ulit') };
      case 'done':
        return { text: tx(lang, 'Done', 'Tapos') };
      default:
        return { text: kind };
    }
  };

  const FootButton = ({ kind, dark }) => {
    const label = buttonLabel(kind);
    return (
      <button type="button" className={`ts-btn ${dark ? 'is-dark' : 'is-ghost'}`} onClick={() => act(kind)}>
        {label.test ? <AssessmentIcon type={label.test} size={18} /> : label.icon ? <Icon name={label.icon} size={18} /> : null}
        <span>{label.text}</span>
      </button>
    );
  };

  const renderAnswerPanel = () => {
    const prompt = pick(config.prompt, lang);
    const options = config.scale;
    // Kept in place (only hidden) when there's nothing to go back to, so the panel never jumps
    const head = (
      <div className="ts-panel-head">
        <p className="ts-prompt">{prompt}</p>
        <button
          type="button"
          className={`ts-back${canChange ? '' : ' is-hidden'}`}
          onClick={change}
          tabIndex={canChange ? 0 : -1}
          aria-hidden={!canChange}
        >
          <Icon name="undo" size={14} />
          {tx(lang, `Back to question ${index}`, `Bumalik sa tanong ${index}`)}
        </button>
      </div>
    );
    // After going back, the answer given before is marked until a new one is picked
    const was = (value) => (backTo && backTo.index === index && backTo.value === value ? ' is-previous' : '');
    const wasLabel = tx(lang, 'Your answer', 'Sagot mo');
    if (config.layout === 'agree') {
      const agree = options.filter((o) => o.value <= 3);
      const neither = options.find((o) => o.value === 4);
      const disagree = options.filter((o) => o.value >= 5);
      const group = (items, label) => (
        <div className="ts-agree-group">
          <span className="ts-label">{label}</span>
          <div className="ts-row3">
            {items.map((o) => (
              <button key={o.value} type="button" className={`ts-opt is-center${picked === o.value ? ' is-picked' : ''}${was(o.value)}`} data-was={wasLabel} onClick={() => answer(o.value)} aria-label={pick(o.label, lang)}>
                <b>{pick(o.button, lang)}</b>
                <kbd className="ts-key" aria-hidden="true">{o.value}</kbd>
              </button>
            ))}
          </div>
        </div>
      );
      return (
        <div className="ts-panel" role="group" aria-label={prompt}>
          {head}
          {group(agree, tx(lang, 'Agree', 'Sang-ayon'))}
          <button type="button" className={`ts-opt is-center is-neither${picked === neither.value ? ' is-picked' : ''}${was(neither.value)}`} data-was={wasLabel} onClick={() => answer(neither.value)}>
            <b>{pick(neither.button, lang)}</b>
            <kbd className="ts-key" aria-hidden="true">{neither.value}</kbd>
          </button>
          {group(disagree, tx(lang, 'Disagree', 'Hindi sang-ayon'))}
        </div>
      );
    }
    if (config.layout === 'yesno') {
      return (
        <div className="ts-panel" role="group" aria-label={prompt}>
          {head}
          <div className="ts-row2">
            {options.map((o) => (
              <button key={o.value} type="button" className={`ts-opt is-center is-big${picked === o.value ? ' is-picked' : ''}${was(o.value)}`} data-was={wasLabel} onClick={() => answer(o.value)}>
                <b>{pick(o.label, lang)}</b>
                <kbd className="ts-key" aria-hidden="true">{o.value === 'yes' ? tx(lang, 'Y', 'O') : tx(lang, 'N', 'H')}</kbd>
              </button>
            ))}
          </div>
          {config.typing && (
            <form className="ts-type" onSubmit={sendTyped}>
              <input
                value={typed}
                onChange={(e) => setTyped(e.target.value)}
                placeholder={pick(config.typing.placeholder, lang)}
                aria-label={pick(config.typing.placeholder, lang)}
                maxLength={80}
              />
              <button type="submit" disabled={!typed.trim()} aria-label={tx(lang, 'Send', 'Ipadala')}>
                <Icon name="send" size={20} />
              </button>
            </form>
          )}
        </div>
      );
    }
    return (
      <div className="ts-panel" role="group" aria-label={prompt}>
        {head}
        <div className={config.layout === 'row' ? 'ts-row3' : 'ts-list'}>
          {options.map((o) => (
            <button
              key={o.value}
              type="button"
              className={`ts-opt${config.layout === 'row' ? ' is-center' : ''}${picked === o.value ? ' is-picked' : ''}${was(o.value)}`}
              data-was={wasLabel}
              onClick={() => answer(o.value)}
            >
              <span className="ts-num">{o.value}</span>
              <span className="ts-opt-text">
                <b>{pick(o.label, lang)}</b>
                {o.sub && <small>{pick(o.sub, lang)}</small>}
              </span>
            </button>
          ))}
        </div>
      </div>
    );
  };

  const renderFoot = () => {
    if (phase === 'intro') {
      return (
        <div className="ts-foot">
          <button type="button" className="ts-btn is-dark" onClick={start}>
            <span>{tx(lang, 'Start', 'Simulan')}</span>
            <Icon name="arrow-right" size={18} />
          </button>
          <button type="button" className="ts-btn is-ghost" onClick={() => act('notnow')}>
            {tx(lang, 'Not now', 'Mamaya na')}
          </button>
        </div>
      );
    }
    if (phase === 'active') {
      return (
        <div className={`ts-foot${thinking ? ' is-waiting' : ''}`} ref={panelRef} onKeyDown={moveFocus}>
          {renderAnswerPanel()}
        </div>
      );
    }
    if (phase === 'blocked') {
      return (
        <div className="ts-foot">
          <button type="button" className="ts-btn is-ghost" onClick={() => act('done')}>
            {tx(lang, 'Done', 'Tapos')}
          </button>
        </div>
      );
    }
    if (phase === 'finished' && plan && !(renderResult && result.status === STATUS.RELEASED && !result.worrying)) {
      return (
        <div className="ts-foot">
          <FootButton kind={plan.primary} dark />
          {plan.secondary.length > 0 && (
            <div className={plan.secondary.length > 1 ? 'ts-btn-row' : ''}>
              {plan.secondary.map((kind) => (
                <FootButton kind={kind} key={kind} />
              ))}
            </div>
          )}
        </div>
      );
    }
    return null;
  };

  // A released result with its own screen (RIASEC's interests and programs) takes over the view
  if (phase === 'finished' && renderResult && result && result.status === STATUS.RELEASED && !result.worrying) {
    return (
      <div className={`ts-view ts-${config.key}`} data-phase="result">
        {renderResult({ result, act, lang, school, context, history, feedRef })}
      </div>
    );
  }

  return (
    <div className={`ts-view ts-${config.key}${entering ? ' is-entering' : ''}`} data-phase={phase}>
      <p className="ts-sr" role="status" aria-live="polite">
        {announcement}
      </p>
      <div className="ts-feed" ref={feedRef}>
        {thread}

        {phase === 'saving' && config.working && working ? (
          !phone && (
            <>
              <BotRow ghost={historyEndsWithAnna}>
                <div className="ts-bubble">{pick(config.working.lead, lang)}</div>
              </BotRow>
              <BotRow ghost wide>
                <div className="ts-card">
                  <WorkingCard working={config.working} step={working.step} slow={working.slow} trouble={net.trouble} lang={lang} />
                </div>
              </BotRow>
            </>
          )
        ) : (
          (thinking || ((phase === 'saving' || phase === 'loading') && !net.trouble)) && (
            <BotRow ghost={historyEndsWithAnna}>
              <div className="ts-bubble is-typing" aria-label={tx(lang, 'Anna is typing', 'Nagta-type si Anna')}>
                <i />
                <i />
                <i />
              </div>
            </BotRow>
          )
        )}

        {phase === 'blocked' && (
          <BotRow>
            <div className="ts-bubble">
              {blockedMessage}{' '}
              {tx(lang,
                'For questions or to ask for more time, talk to the guidance office.',
                'Para sa mga tanong o para humingi ng dagdag na oras, kausapin ang guidance office.')}
            </div>
          </BotRow>
        )}

        {net.trouble && phase !== 'finished' && phase !== 'blocked' && !(config.working && working) && <WaitingNote lang={lang} trouble={net.trouble} />}

        {phase === 'finished' && renderFinish()}
      </div>
      {renderFoot()}
      {phase === 'saving' && config.working && working && phone && (
        <div className="ts-working-screen">
          <WorkingCard working={config.working} step={working.step} slow={working.slow} trouble={net.trouble} lang={lang} />
        </div>
      )}
    </div>
  );
};

export default TestChat;
